import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LapTimeChart from "../components/LapTimeChart";
import SetupValuesSection from "../components/SetupValuesSection";
import TireVisual from "../components/TireVisual";
import WeatherBadge from "../components/WeatherBadge";
import { useAuthStore } from "../state/authStore";
import { useRunStore } from "../state/runStore";
import type { RunDetail as RunDetailType } from "../types";
import { api, downloadFile, uploadFile } from "../utils/api";
import { formatFalloff, formatFuelAmount, formatLapTime, formatSetupName, fuelUnitForDisplay, fuelValueForDisplay } from "../utils/formatters";

function mean(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}
function stdDev(xs: number[]): number | null {
  if (xs.length < 2) return null;
  const m = mean(xs);
  return Math.sqrt(mean(xs.map((x) => (x - m) * (x - m))));
}

export default function RunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { fetchRunDetail } = useRunStore();
  const { user } = useAuthStore();

  const [run, setRun] = useState<RunDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupChangesDraft, setSetupChangesDraft] = useState("");
  const [commentsDraft, setCommentsDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesStatus, setNotesStatus] = useState<string | null>(null);
  const [stoBusy, setStoBusy] = useState<"download" | "upload" | null>(null);
  const [stoStatus, setStoStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    fetchRunDetail(id)
      .then((data) => { if (!cancelled) setRun(data); })
      .catch(() => { if (!cancelled) setRun(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
    };
  }, [id, fetchRunDetail]);

  useEffect(() => {
    setSetupChangesDraft(run?.setupChanges || "");
    setCommentsDraft(run?.comments || "");
    setNotesStatus(null);
  }, [run?.id, run?.setupChanges, run?.comments]);

  const isOwnRun = useMemo(() => user?.id === run?.userId, [user?.id, run?.userId]);

  const handleDownloadSto = async () => {
    if (!run) return;
    setStoBusy("download");
    setStoStatus(null);
    try {
      await downloadFile(`/api/runs/${run.id}/setup-file`, run.stoFilename || `${run.setupName || "setup"}.sto`);
    } catch (err) {
      setStoStatus(err instanceof Error ? err.message : "Failed to download setup file");
    } finally {
      setStoBusy(null);
    }
  };

  const handleAttachSto = async (file: File) => {
    if (!run || !isOwnRun) return;
    if (!file.name.toLowerCase().endsWith(".sto")) {
      setStoStatus("Only .sto files can be attached.");
      return;
    }
    setStoBusy("upload");
    setStoStatus(null);
    try {
      const resp = (await uploadFile(`/api/runs/${run.id}/setup-file`, file)) as unknown as {
        stoFilename?: string;
      };
      setRun({ ...run, hasSto: true, stoFilename: resp.stoFilename ?? file.name });
      setStoStatus("Setup file attached — teammates can now download it.");
    } catch (err) {
      setStoStatus(err instanceof Error ? err.message : "Failed to attach setup file");
    } finally {
      setStoBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="page page--wide">
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading run</div>
          <div className="empty-state-text">Pulling lap data, notes, tire information, and weather context.</div>
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="page page--wide">
        <div className="empty-state">
          <div className="empty-state-title">Run not found</div>
          <div className="empty-state-text">This run could not be loaded or may no longer exist.</div>
        </div>
      </div>
    );
  }

  const handleSaveNotes = async () => {
    if (!isOwnRun || !run) return;
    setSavingNotes(true);
    setNotesStatus(null);
    try {
      await api(`/api/runs/${run.id}`, {
        method: "PUT",
        body: JSON.stringify({
          setup_changes: setupChangesDraft.trim() || null,
          comments: commentsDraft.trim() || null,
        }),
      });
      setRun({
        ...run,
        setupChanges: setupChangesDraft.trim() || null,
        comments: commentsDraft.trim() || null,
      });
      setNotesStatus("Saved");
    } catch (err) {
      setNotesStatus(err instanceof Error ? err.message : "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // Pace & Consistency (#208) — all from fields already on the run.
  const greenValidTimes = run.laps
    .filter((l) => l.isGreen && l.isValid && l.lapTime != null)
    .map((l) => l.lapTime as number);
  const lapSpread = stdDev(greenValidTimes);
  const bestVsAvg =
    run.bestLapTime != null && run.avgLapTime != null ? run.avgLapTime - run.bestLapTime : null;

  // Fuel burn range (#210) — clean green-lap fuelUsed only, no race-length input.
  // Match the page's pace/chart green filter (isValid + no pit laps) so the min
  // floor can't land on a coast/lift/pit-entry lap and inflate the save target.
  const greenFuel = run.laps
    .filter(
      (l) =>
        l.isGreen &&
        l.isValid &&
        !l.pitIn &&
        !l.pitOut &&
        l.fuelUsed != null &&
        (l.fuelUsed as number) > 0,
    )
    .map((l) => l.fuelUsed as number);
  const fuelUnit = fuelUnitForDisplay(run.weather?.units);
  const fmtFuel = (v: number) => (fuelValueForDisplay(v, run.weather?.units) ?? 0).toFixed(2);
  const burnRange = greenFuel.length
    ? `${fmtFuel(Math.min(...greenFuel))} · ${fmtFuel(mean(greenFuel))} · ${fmtFuel(Math.max(...greenFuel))} ${fuelUnit}`
    : "—";
  let burnTrend: string | null = null;
  if (greenFuel.length >= 6) {
    const t = Math.floor(greenFuel.length / 3);
    const d = fuelValueForDisplay(mean(greenFuel.slice(-t)) - mean(greenFuel.slice(0, t)), run.weather?.units) ?? 0;
    burnTrend = Math.abs(d) < 0.005 ? "holds steady" : `${d > 0 ? "creeping +" : "easing "}${Math.abs(d).toFixed(3)}/lap`;
  }
  // Save target (#210) — the per-lap fuel you could reclaim by matching your own
  // leanest green lap (avg − min). No race-length/tank input, so it can never
  // disagree with the Fuel planner; the floor it measures against is the same
  // min already shown in the burn range.
  let saveTarget: string | null = null;
  if (greenFuel.length >= 4) {
    const gap = fuelValueForDisplay(mean(greenFuel) - Math.min(...greenFuel), run.weather?.units) ?? 0;
    saveTarget = gap >= 0.005 ? `${gap.toFixed(3)} ${fuelUnit}/lap` : "at your leanest";
  }

  return (
    <div className="page page--wide">
      <button type="button" className="back-link" onClick={() => navigate(-1)}>
        &larr; Back
      </button>

      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">
              {[
                run.eventType || null,
                run.sessionDate ? new Date(run.sessionDate).toLocaleDateString() : null,
                run.setupName ? formatSetupName(run.setupName) : null,
              ].filter(Boolean).join("  ·  ")}
            </span>
          </div>
          <div className="editorial-meta-right" style={styles.headerPills}>
            {run.runStatus && <span className="pill">{run.runStatus.replace(/_/g, " ")}</span>}
            {run.dataQualityScore != null && (
              <span className={`pill ${run.dataQualityScore >= 70 ? "pill--success" : run.dataQualityScore >= 50 ? "pill--warning" : "pill--danger"}`}>
                {Math.round(run.dataQualityScore)} quality
              </span>
            )}
            {run.greenLaps != null && <span className="pill">{run.greenLaps} green laps</span>}
            {run.weather && <WeatherBadge weather={run.weather} />}
          </div>
        </div>
        <h1 className="editorial-title">{run.displayName || "Unknown driver"}</h1>
      </header>

      <div style={styles.metricGrid}>
        <MetricCard label="Best lap" value={formatLapTime(run.bestLapTime)} accent />
        <MetricCard label="Average lap" value={formatLapTime(run.avgLapTime)} />
        <MetricCard label="Fuel / lap" value={formatFuelAmount(run.fuelPerLap, run.weather?.units, 3)} />
        <MetricCard label="Falloff" value={formatFalloff(run.falloffMsPerLap)} />
      </div>

      <SetupValuesSection
        runId={run.id}
        teamId={run.teamId}
        trackWeekId={run.trackWeekId}
        setupName={run.setupName ?? null}
        hasSto={!!run.hasSto}
        stoFilename={run.stoFilename ?? null}
        isOwnRun={isOwnRun}
        tireData={run.tireData}
        unit={run.weather?.units}
        stoBusy={stoBusy}
        stoStatus={stoStatus}
        onDownload={handleDownloadSto}
        onAttach={handleAttachSto}
      />

      <section className="section-shell">
        <div className="section-header">
          <div>
            <div className="section-kicker">Driver Notes</div>
            <div className="section-heading">Setup changes and notes attached to this run.</div>
          </div>
        </div>

        <div className="card" style={styles.notesCard}>
          <label style={styles.notesField}>
            <span style={styles.notesLabel}>Setup changes</span>
            {isOwnRun ? (
              <textarea
                value={setupChangesDraft}
                onChange={(event) => setSetupChangesDraft(event.target.value)}
                placeholder="Log spring, shock, bar, pressure, and balance changes here."
                rows={5}
              />
            ) : (
              <div style={styles.notesReadOnly}>{run.setupChanges || "No setup changes logged."}</div>
            )}
          </label>

          <label style={styles.notesField}>
            <span style={styles.notesLabel}>Comments</span>
            {isOwnRun ? (
              <textarea
                value={commentsDraft}
                onChange={(event) => setCommentsDraft(event.target.value)}
                placeholder="Add driver feel, reminders, and follow-up notes."
                rows={5}
              />
            ) : (
              <div style={styles.notesReadOnly}>{run.comments || "No comments saved."}</div>
            )}
          </label>

          {isOwnRun && (
            <div style={styles.notesFooter}>
              <span style={styles.notesStatus}>{notesStatus || "Notes are visible to the whole team."}</span>
              <button type="button" className="btn btn-primary" onClick={handleSaveNotes} disabled={savingNotes}>
                {savingNotes ? "Saving…" : "Save notes"}
              </button>
            </div>
          )}
        </div>
      </section>

      <div style={styles.mainGrid}>
        <section className="card" style={styles.chartSection}>
          <div style={styles.sectionLabel}>Lap Times & Pacing</div>
          <LapTimeChart laps={run.laps} bestLapTime={run.bestLapTime} />
        </section>

        <section className="card" style={styles.sideCard}>
          <div style={styles.sectionLabel}>Tire Data</div>
          <TireVisual tireData={run.tireData} unit={run.weather?.units} />
        </section>

        <div style={styles.twoUp}>
          <section className="card" style={styles.sideCard}>
            <div style={styles.sectionLabel}>Fuel</div>
            <div style={styles.fuelStack}>
              <FuelRow label="Per lap" value={formatFuelAmount(run.fuelPerLap, run.weather?.units, 3)} />
              <FuelRow label={`Burn / lap, min avg max (${fuelUnit})`} value={burnRange} />
              {burnTrend && <FuelRow label="Burn trend" value={burnTrend} />}
              {saveTarget && <FuelRow label="Save vs leanest lap" value={saveTarget} />}
              <FuelRow label="Max laps" value={run.maxLapsOnFuel != null ? run.maxLapsOnFuel.toFixed(1) : "—"} />
              <FuelRow label="Green / total" value={`${run.greenLaps ?? 0} / ${run.totalLaps ?? 0}`} />
            </div>
          </section>

          <section className="card" style={styles.sideCard}>
            <div style={styles.sectionLabel}>Pace & Consistency</div>
            <div style={styles.fuelStack}>
              <FuelRow label="Top 10 avg" value={formatLapTime(run.top10Avg)} />
              <FuelRow label="Best vs avg" value={bestVsAvg != null ? `+${bestVsAvg.toFixed(3)}s` : "—"} />
              <FuelRow label="Lap spread" value={lapSpread != null ? `±${lapSpread.toFixed(3)}s` : "—"} />
              <FuelRow label="Falloff / lap" value={formatFalloff(run.falloffMsPerLap)} />
            </div>
          </section>
        </div>
      </div>

      <section className="section-shell">
        <div className="section-header">
          <div>
            <div className="section-kicker">Laps</div>
            <div className="section-heading">Full lap-by-lap record.</div>
          </div>
        </div>

        <div className="card xpe-table-scroll" style={{ padding: 0, overflow: "hidden" }}>
          <table className="table striped">
            <thead>
              <tr>
                <th>Lap</th>
                <th>Time</th>
                <th>Status</th>
                <th>Flag</th>
                <th>Pit</th>
                <th>Fuel Used ({fuelUnitForDisplay(run.weather?.units)})</th>
              </tr>
            </thead>
            <tbody>
              {run.laps.map((lap) => {
                const bestLap = run.bestLapTime != null && lap.lapTime === run.bestLapTime;
                return (
                  <tr key={lap.lapNum}>
                    <td>{lap.lapNum}</td>
                    <td className={bestLap ? "lap-time lap-time-pb" : "lap-time"}>{formatLapTime(lap.lapTime)}</td>
                    <td>{lap.isValid ? <span className="badge badge-green">Valid</span> : <span className="badge badge-yellow">Invalid</span>}</td>
                    <td>{lap.isGreen ? <span className="badge badge-green">Green</span> : <span className="badge badge-yellow">Yellow</span>}</td>
                    <td>{lap.pitIn || lap.pitOut ? <span className="badge badge-blue">Pit</span> : "—"}</td>
                    <td className="mono">
                      {lap.fuelUsed != null ? fuelValueForDisplay(lap.fuelUsed, run.weather?.units)?.toFixed(3) ?? "-" : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="card" style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={{
        ...styles.metricValue,
        color: accent ? "var(--live)" : "var(--text-strong)",
      }}>{value}</div>
    </div>
  );
}

function FuelRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.fuelRow}>
      <span style={styles.fuelLabel}>{label}</span>
      <span style={styles.fuelValue}>{value}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  headerPills: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
    marginTop: "0.75rem",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "0.85rem",
    marginBottom: "1.25rem",
  },
  metricCard: {
    padding: "1.1rem 1.15rem",
  },
  metricLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    marginBottom: "0.5rem",
  },
  metricValue: {
    fontFamily: "var(--font-mono)",
    fontSize: "1.6rem",
    fontWeight: 700,
    lineHeight: 1.08,
  },
  mainGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    marginBottom: "0.5rem",
  },
  chartSection: {
    padding: "1.25rem",
  },
  twoUp: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "1rem",
    alignItems: "start",
  },
  sideCard: {
    padding: "1.25rem",
  },
  sectionLabel: {
    color: "var(--text-strong)",
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    marginBottom: "0.75rem",
  },
  fuelStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0.85rem",
  },
  fuelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    paddingBottom: "0.85rem",
    borderBottom: "1px solid var(--rule-faint)",
  },
  fuelLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  fuelValue: {
    color: "var(--text-strong)",
    fontFamily: "var(--font-mono)",
    fontSize: "1rem",
    fontWeight: 600,
  },
  notesCard: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  notesField: {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
  },
  notesLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  notesReadOnly: {
    minHeight: "7.5rem",
    padding: "0.9rem 1rem",
    borderRadius: "var(--r-md)",
    background: "var(--ink-1)",
    border: "1px solid var(--rule)",
    color: "var(--text-default)",
    lineHeight: 1.7,
  },
  notesFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: "wrap",
  },
  notesStatus: {
    color: "var(--text-secondary)",
    lineHeight: 1.6,
  },
};
